import { KnowledgeDocument } from '../types/Knowledge';

export type KnowledgeFileKind = 'pdf' | 'doc' | 'json' | 'image' | 'text';

export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

export const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const truncateUrl = (url: string, maxLength = 42): string => {
  if (!url || url.length <= maxLength) {
    return url;
  }

  return `${url.slice(0, maxLength - 1)}…`;
};

export const getFileKind = (document: KnowledgeDocument): { kind: KnowledgeFileKind; label: string } => {
  const mime = document.mimeType.toLowerCase();
  const name = document.originalFileName.toLowerCase();

  if (mime.includes('pdf') || name.endsWith('.pdf')) {
    return { kind: 'pdf', label: 'PDF' };
  }

  if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) {
    return { kind: 'doc', label: 'DOC' };
  }

  if (mime.includes('json') || name.endsWith('.json')) {
    return { kind: 'json', label: 'JSON' };
  }

  if (mime.startsWith('image/')) {
    return { kind: 'image', label: 'IMG' };
  }

  return { kind: 'text', label: 'TXT' };
};

/** The link worth showing and copying: the short redirect when available, else the direct storage URL. */
export const getShareableUrl = (document: KnowledgeDocument): string => document.shareUrl || document.publicUrl;
